import { z } from "zod/v4";

import { getVmNameIssue } from "@/lib/vm-name";

/**
 * Form schema for the "Provision VM for user" page.
 * The resource-item specifications are built from these fields in onSubmit.
 */
export const provisionVmForUserSchema = z.object({
  targetTenantId: z.string().min(1, "Tenant is required"),
  targetUserId: z.string().min(1, "User is required"),
  // The handler reuses the customer order command, so the name faces the backend's full hostname
  // rules (letters, digits, hyphens and dots; no underscores; no reserved domain). Checking them
  // here turns a 400 from the order command into an inline field error.
  vmName: z.string().superRefine((value, ctx) => {
    const issue = getVmNameIssue(value);
    if (issue) {
      ctx.addIssue({ code: "custom", message: issue });
    }
  }),
  cores: z.coerce.number().int().min(1, "At least 1 vCPU").max(64),
  /** Memory in MB — the unit the order expects. The admin form edits it in GB. */
  memory: z.coerce
    .number()
    .int()
    .min(512, "Memory must be at least 512 MB (0.5 GB)")
    .max(262144, "Memory cannot exceed 262144 MB (256 GB)"),
  // The handler splits the boot disk into its own Storage resource, so a VM without one has no
  // disk attachment and the provisioning job refuses to substitute a default. The order validator
  // rejects a non-positive disk server-side; keeping the same bound here surfaces it on the field
  // instead of as an opaque 400 from the debounced price preview.
  disk: z.coerce.number().int().min(1, "Disk must be at least 1 GB").max(10000),
  /**
   * Total public IPs, including the VM's own. The pricing engine charges
   * PriceFirstIp + PriceAdditionalIp * (count - 1) and defaults to a single IP when the value is
   * absent, so the field always sends a count rather than a number of extras. The 1..10 bounds
   * mirror ProductLineSpecConfig.MinIps/MaxIps on the backend.
   */
  additionalIps: z.coerce
    .number()
    .int()
    .min(1, "At least 1 public IP")
    .max(10, "At most 10 public IPs"),
  /**
   * Backup and snapshot slots, sent EXPLICITLY on every request even when zero.
   *
   * An absent `num-backups` / `num-snapshots` does not mean "none": the pricing engine substitutes
   * the product line's configured default — or the regional `ProductLinePrice` row's, which is
   * higher still — and prices that. Two ways that goes wrong, both measured on DEV:
   *
   * - Default OUTSIDE the tier's specLimits: the order is rejected outright. Performance caps
   *   backups at 0 but its vn-south-1 row defaults to 3, so omitting the key answers 400.
   * - Default INSIDE the limits: it is charged although nobody asked for it. Cheap quoted 343,000
   *   VND with `num-snapshots: 0` and 383,000 VND with the key absent.
   *
   * The client wizard always sends both (see `transformToPricingConfig`), so the admin form must
   * too — otherwise the same VM either fails or costs more when created from here.
   */
  backupCount: z.coerce
    .number()
    .int()
    .min(0, "Backup slots cannot be negative")
    .max(7, "At most 7 backup slots"),
  snapshotCount: z.coerce
    .number()
    .int()
    .min(0, "Snapshot slots cannot be negative")
    .max(7, "At most 7 snapshot slots"),
  region: z.string().min(1, "Region is required"),
  productLine: z.string().min(1, "Product line is required"),
  /**
   * CPU type the VM is placed on. Optional: when omitted the backend derives one — the product
   * line's first enabled chip type, or a supported chip type that maps to a pool when the product
   * line has none enabled. The page still sends it whenever the product line does have chip types
   * enabled, so the operator rather than the fallback picks the CPU, and so the preview and the
   * real order price the same configuration.
   *
   * productLine + chipType together resolve the provisioning pool. Historically a missing chipType
   * silently placed the VM on the provider's default pool while billing the selected tier; the
   * order handler now derives it and rejects a pair that maps to no pool.
   */
  chipType: z.string().optional(),
  templateId: z.string().min(1, "OS template is required"),
  billingCycleMonths: z.coerce.number().int().min(1).max(36),
  /**
   * Optional sub-monthly term in whole days. When set it REPLACES the month cycle: the backend
   * pins months to 1, pro-rates the price from the monthly rate (rate x days / 30, no commitment
   * discount) and dates the VM's billing cycle exactly `days` days from provisioning. Capped at 30
   * so it never overlaps the month range. Undefined (or 0) for the normal month-based cycle —
   * 0 is treated as "not set", never rejected.
   */
  billingCycleDays: z.preprocess(
    (v) => (v === 0 || v === "0" ? undefined : v),
    z.coerce
      .number()
      .int()
      .min(1, "At least 1 day")
      .max(30, "At most 30 days")
      .optional(),
  ),
  adminNotes: z.string().max(500).optional(),
});

export type ProvisionVmForUserForm = z.infer<typeof provisionVmForUserSchema>;
